// Import the functions you need from the SDKs you need
import axios from "axios";
import { notify } from "../components/AppNotifications";
import { getAnalytics, logEvent, setUserId } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { getDatabase } from "firebase/database";
import {
  doc,
  getDoc,
  getFirestore
} from "firebase/firestore";
import { getStorage, getDownloadURL, ref } from "firebase/storage";

// import { getAuth as getAdminAuth, createCustomToken } from "firebase-admin/auth";
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};
export const Base_Url = process.env.REACT_APP_API_URL

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
// export const adminAuth = getAdminAuth();
export const auth = getAuth(app);
export const db = getFirestore(app);
export const database = getDatabase(app);
export const storage = getStorage(app);

// Policy document (WHPolicy.pdf) lives at the root of the project's Storage
// bucket. Resolve it from the *current environment's* bucket (dev vs prod) via
// getDownloadURL rather than hardcoding the dev bucket + a per-object token —
// the download token differs per object, so the prod copy needs its own URL.
export const POLICY_PDF_FILENAME = "WHPolicy.pdf";
export const getPolicyPdfUrl = async () => {
  return await getDownloadURL(ref(storage, POLICY_PDF_FILENAME));
};

// Exported now: several routes that were open to the internet are being
// admin-gated server-side, and their call sites live in other modules.
export const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};

// Firebase auth codes are not user-facing ("Firebase: Error (auth/wrong-password)").
const AUTH_ERROR_MESSAGES = {
  'auth/invalid-email': 'That email address doesn\'t look right. Please check it and try again.',
  'auth/user-not-found': 'We couldn\'t find an account with that email address.',
  'auth/wrong-password': 'Incorrect password. Please try again, or use Forgot Password to reset it.',
  'auth/invalid-credential': 'Incorrect email or password. Please try again, or use Forgot Password to reset it.',
  'auth/invalid-login-credentials': 'Incorrect email or password. Please try again, or use Forgot Password to reset it.',
  'auth/too-many-requests': 'Too many sign-in attempts. Please wait a few minutes and try again, or reset your password.',
  'auth/user-disabled': 'This account has been disabled. Please contact an administrator.',
  'auth/network-request-failed': 'Network problem — please check your connection and try again.',
};

export const logInWithEmailAndPassword = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    console.error(err);
    trackEventAnalytics({ error: JSON.stringify(err) })
    notify(AUTH_ERROR_MESSAGES[err?.code] || err.message, 'error', 'Sign-in failed');
  }
};
export const registerWithEmailAndPassword = async (name, email, password) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    // await addDoc(collection(db, "users"), {
    //   uid: user.uid,
    //   name,
    //   authProvider: "local",
    //   email,
    // });
    return user;
  } catch (err) {
    trackEventAnalytics({ error: JSON.stringify(err) })
    console.error(err);
    alert(err.message);
    throw err
  }
};

export const registerWithEmailAndPassword2 = async (name, email, password) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    return user;
  } catch (err) {
    trackEventAnalytics({ error: JSON.stringify(err) })
    throw err
  }
};
export const apiRegisterWithEmailAndPassword2 = async (email, password) => {
  try {
    // Sent now that /createNewUser is admin-gated. It was unauthenticated,
    // which let anyone on the internet create Firebase Auth accounts in the
    // project.
    const token = await getAuthToken();
    const response = await axios.post(`${Base_Url}/api/createNewUser`, {
      email: email,
      password: password,
    }, { headers: { Authorization: `Bearer ${token}` } });
    if (response.data.error) {
      throw response.data.error
    } else {
      const user = response.data.data || {};
      // `_existed` is true when the Auth account already existed (orphan recovery)
      // so the caller can avoid overwriting an active member.
      user._existed = response.data.existed === true;
      return user;
    }
  } catch (err) {
    trackEventAnalytics({ error: JSON.stringify(err) })
    throw err
  }
};

// Best-effort rollback of a just-created Auth user when the follow-up Firestore
// write fails, so we don't leave an orphaned account that blocks future retries.
export const apiDeleteUserAuth = async (uid) => {
  try {
    await axios.delete(`${Base_Url}/api/deleteUserAuth`, { params: { uuid: uid } });
  } catch (err) {
    trackEventAnalytics({ error: JSON.stringify(err) })
  }
};

export const createCustomerSubscriptionPOSTRequest = async (priceToken, customerId) => {
  try {
    const token = await getAuthToken();

    const response = await axios.post(
      `${Base_Url}/stripeApi/createCustomerSubscriptionV2`,
      {
        priceToken,
        customerId,
        metadata: { isPaymentFrom: "Subscriptions" }
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        }
      }
    );

    if (response.data.error) {
      throw response.data.error;
    }

    return response.data.data;

  } catch (err) {
    trackEventAnalytics({ error: JSON.stringify(err) });
    throw err;
  }
};



export const sendPasswordReset = async (email) => {
  try {
    // Deliver the reset email via Klaviyo (Firebase's built-in delivery is
    // unreliable — Asana #5). Backend mints the reset link + triggers the
    // Klaviyo "Password Reset Requested" flow.
    await axios.post(`${Base_Url}/api/requestPasswordReset`, { email });
    return true
  } catch (err) {
    trackEventAnalytics({ error: JSON.stringify(err) })
    console.error(err);
    alert(err?.response?.data?.error?.message || err.message || 'Failed to send reset email');
    return false
  }
};
// Route an admin-originated member email through Klaviyo via the backend
// `api` function (same pattern as sendPasswordReset — the Klaviyo private key
// stays on the server). The backend maps `type` to a live Klaviyo metric ->
// flow -> branded template. See WHAdminAPI /sendAppEvent.
//   type       - 'staffAlert' | 'orderReceipt' | 'rsvpConfirmation'
//   to         - recipient email string, or array of emails
//   properties - structured template variables (Klaviyo escapes raw HTML, so
//                send DATA, not HTML)
// Non-fatal by design: email delivery must never block the admin's action.
export const sendKlaviyoAppEvent = async (type, to, properties = {}) => {
  try {
    const res = await axios.post(`${Base_Url}/api/sendAppEvent`, { type, to, properties });
    return res && res.data;
  } catch (err) {
    console.error('sendKlaviyoAppEvent error:', err?.response?.data || err.message);
    return null;
  }
};

export const logout = () => {
  signOut(auth);
};


export const userAnalytics = (user) => {
  setUserId(analytics, user.uid);
}

export const trackEventAnalytics = (obj) => {
  let analyticsObj = obj
  try {
    const analytics = getAnalytics();
    logEvent(analytics, 'error_content', {
      obj
    });
  } catch (err) {
  }
};

export const trackException = (desc) => {
  try {
    const analytics = getAnalytics();
    logEvent(analytics, 'exception', {
      description: desc
    });
  } catch (err) {
  }
}

let cachedTimeZones = null;
export const DEFAULT_TIME_ZONE = 'America/Denver'; // MST

export async function getUSTimeZones() {
  // ✅ Return from memory if already loaded
  if (cachedTimeZones) {
    return cachedTimeZones;
  }

  try {
    const ref = doc(db, "AppSettings", "timeZones");
    const snap = await getDoc(ref);

    if (snap.exists()) {
      cachedTimeZones = snap.data().usTimeZones || [];
      return cachedTimeZones;
    }

    return [];
  } catch (error) {
    console.error("Failed to fetch time zones", error);
    return [];
  }
}